import { Link } from 'react-router-dom';
import { Briefcase, Eraser, Loader2, Send } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Pill } from '../../../components/ui/Pill';

export interface SystemPerson {
  id: string;
  full_name: string;
  position: string | null;
  start_date: string | null;
  employee_id: string | null;
  sap_status: 'not_ready' | 'ready' | 'running' | 'failed' | 'done';
  missing: string[];
  last_run: { status: string; dry_run: boolean; error: string | null; at: string } | null;
  planon_status: 'not_ready' | 'ready' | 'done';
}

type StageState = 'done' | 'active' | 'failed' | 'todo';

/** The three pipeline stages derived from the person's SAP state. */
function stages(p: SystemPerson): { label: string; state: StageState }[] {
  const dataDone = p.sap_status !== 'not_ready';
  const sap: StageState =
    p.sap_status === 'done'
      ? 'done'
      : p.sap_status === 'failed'
        ? 'failed'
        : p.sap_status === 'running'
          ? 'active'
          : 'todo';
  const planon: StageState = p.planon_status === 'done' ? 'done' : 'todo';
  return [
    { label: 'Data', state: dataDone ? 'done' : 'failed' },
    { label: 'SAP', state: sap },
    { label: 'Planon', state: planon },
  ];
}

const STAGE_DOT: Record<StageState, string> = {
  done: 'bg-success',
  active: 'bg-accent aurora-pulse',
  failed: 'bg-danger',
  todo: 'bg-line/30',
};

function ProgressStages({ person }: { person: SystemPerson }) {
  const s = stages(person);
  return (
    <div className="flex items-center gap-1.5" aria-label="pipeline progress">
      {s.map((stage, i) => (
        <div key={stage.label} className="flex items-center gap-1.5">
          {i > 0 ? <div className="h-px w-4 bg-line/30" /> : null}
          <span className={`h-2.5 w-2.5 rounded-full ${STAGE_DOT[stage.state]}`} />
          <span className="text-xs text-fg-muted">{stage.label}</span>
        </div>
      ))}
    </div>
  );
}

const STATUS_PILL: Record<
  SystemPerson['sap_status'],
  { tone: 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral'; label: string }
> = {
  done: { tone: 'success', label: 'in SAP' },
  ready: { tone: 'info', label: 'ready' },
  running: { tone: 'accent', label: 'pushing…' },
  failed: { tone: 'danger', label: 'failed' },
  not_ready: { tone: 'neutral', label: 'data incomplete' },
};

export function SystemCreationRow({
  person,
  pushing,
  pushingPlanon,
  onPush,
  onPushPlanon,
  onEditEmployment,
  onShowMissing,
  onClearEmployeeId,
}: {
  person: SystemPerson;
  pushing: boolean;
  pushingPlanon: boolean;
  onPush: () => void;
  onPushPlanon: () => void;
  onEditEmployment: () => void;
  onShowMissing: () => void;
  onClearEmployeeId: () => void;
}) {
  // Once Planon is done the person is through every system, so the pill
  // reflects the whole pipeline rather than just the SAP stage.
  const pill =
    person.planon_status === 'done'
      ? ({ tone: 'success', label: 'fully created' } as const)
      : STATUS_PILL[person.sap_status];
  const canPush = (person.sap_status === 'ready' || person.sap_status === 'failed') && !pushing;
  const canPushPlanon = person.planon_status === 'ready' && !pushingPlanon;
  const hint =
    person.sap_status === 'not_ready'
      ? `Missing: ${person.missing.slice(0, 4).join(', ')}${person.missing.length > 4 ? '…' : ''}`
      : person.sap_status === 'failed'
        ? (person.last_run?.error ?? 'last run failed')
        : null;

  return (
    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/admin/people/${person.id}?tab=sap`}
            className="truncate text-sm font-medium hover:text-accent hover:underline"
          >
            {person.full_name}
          </Link>
          {person.sap_status === 'not_ready' ? (
            <button
              type="button"
              onClick={onShowMissing}
              title="See what data is missing"
              className="rounded-full outline-none ring-accent/40 transition hover:opacity-80 focus-visible:ring-2"
            >
              <Pill tone={pill.tone}>{pill.label}</Pill>
            </button>
          ) : (
            <Pill tone={pill.tone}>{pill.label}</Pill>
          )}
        </div>
        <div className="truncate text-xs text-fg-dim">
          {person.position ?? 'no position'}
          {hint ? <span className="text-warning-soft-fg"> · {hint}</span> : null}
        </div>
      </div>
      <div className="sm:w-52">
        <ProgressStages person={person} />
      </div>
      <div className="flex items-center gap-2 text-sm tabular-nums text-fg-muted sm:w-36">
        <span className="truncate">{person.employee_id ?? '—'}</span>
        {person.employee_id ? (
          <button
            type="button"
            onClick={onClearEmployeeId}
            title="Clear this employee ID (for dummy / imported codes)"
            aria-label={`Clear employee ID for ${person.full_name}`}
            className="flex-none rounded-md border border-line/20 p-1 text-fg-muted transition hover:bg-danger-soft/15 hover:text-danger-soft-fg"
          >
            <Eraser className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <div className="flex items-center justify-end gap-2 whitespace-nowrap sm:w-80">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEditEmployment}
          title="Edit the SAP employment record (org centre, job, hours...)"
          aria-label="Edit the SAP employment record"
        >
          <Briefcase className="h-4 w-4" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!canPush}
          iconLeft={
            pushing || person.sap_status === 'running' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )
          }
          onClick={onPush}
          title={
            person.sap_status === 'done'
              ? 'Already in SAP'
              : person.sap_status === 'not_ready'
                ? 'Complete the missing data first'
                : person.sap_status === 'failed'
                  ? 'Resume the failed push'
                  : 'Dry run, then push to SAP'
          }
        >
          {person.sap_status === 'failed' ? 'Resume SAP push' : 'Push to SAP'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!canPushPlanon}
          iconLeft={
            pushingPlanon ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )
          }
          onClick={onPushPlanon}
          title={
            person.planon_status === 'done'
              ? 'Already in Planon'
              : person.planon_status === 'not_ready'
                ? 'Create in SAP first'
                : 'Create this person in Planon'
          }
        >
          {person.planon_status === 'done' ? 'In Planon' : 'Push to Planon'}
        </Button>
      </div>
    </div>
  );
}
